import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { distinctUntilChanged, filter, map, Observable, shareReplay, startWith } from 'rxjs';

import {
  readVisibilityTestQueryFromUrl,
  resolveVisibilityTestMode,
} from './visibility-test-mode.util';

/**
 * Storefront visibility test mode, toggled with `?test=true` (and `?test=false`).
 * Internal Angular navigations often drop query params; once enabled, the mode
 * stays on for the current SPA session until `test=false` or a full reload
 * without the param.
 */
@Injectable({ providedIn: 'root' })
export class VisibilityTestModeService {
  private readonly router = inject(Router);
  private enabled = false;

  readonly isTestMode$: Observable<boolean> = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    map(event => event.urlAfterRedirects),
    startWith(null as string | null),
    map(url => {
      const resolvedUrl =
        url ?? (typeof window !== 'undefined' ? window.location.href : this.router.url);
      this.enabled = resolveVisibilityTestMode(
        readVisibilityTestQueryFromUrl(resolvedUrl),
        this.enabled,
      );
      return this.enabled;
    }),
    distinctUntilChanged(),
    shareReplay(1),
  );

  constructor() {
    this.isTestMode$.subscribe();
  }
}
